import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { theme } from 'styled-tools'

import * as badaAppointmentApi from '../../../utils/badaAppointmentApi'
import Loader from '../loader/Loader'
import ArrowLeftIcon from '../icons/arrow-left'
import LocationIcon from '../icons/location'
import ArrowRightIcon from '../icons/arrow-right'
import { Input } from './Input'

const InputField = styled(Input)`
  text-overflow: ellipsis;
`

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`

/* eslint-disable prefer-template */
const SearchResult = styled.ul`
  font-size: ${theme('typography.paragraph.fontSize') || '16px'};
  position: absolute;
  z-index: 10;
  background-color: #fff;
  padding: 0;
  margin: 0;
  margin-top: ${(p) => '-' + p.theme.forms.marginBottom};
  width: 100%;
  list-style-type: none;
  border-style: solid;
  border-width: 1px;
  border-color: ${(p) => p.theme.forms.colors.border};
  max-height: 400px;
  overflow-y: scroll;
  top: 65px;
`
/* eslint-enable prefer-template */

const ResultItem = styled.li`
  cursor: pointer;
  padding: 7px;
  border-width: 1px;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0);
  display: flex;
  justify-content: space-between;

  &:hover {
    border-color: ${(p) => p.theme.forms.colors.border};
  }

  :nth-child(even) {
    background-color: ${(p) => p.theme.colors.secondaryBackgroundColor};
  }

  svg {
    display: flex;
    align-self: center;
  }
`

const StyledLocationWidthIcon = styled.div`
  display: flex;

  svg {
    margin-right: 7px;
    display: flex;
    align-self: flex-start;
  }
`

const addressCache = new Map()

/**
 * @param {{
 *  selectedAddress: {
 *    address: string
 *    address2: string
 *    address3: string
 *    postalCode: string
 *    city: string
 *    country: string
 *  }
 *  onClearAddress: () => void
 *  onSelectAddress: (address: {
 *    address: string
 *    address2: string
 *    address3: string
 *    postalCode: string
 *    city: string
 *    country: string
 *  }) => void
 *  onBlur: (e: Event) => void
 * }} props
 */
export const AddressInput = ({
  selectedAddress,
  onBlur,
  onClearAddress,
  onSelectAddress,
}) => {
  const [query, setQuery] = useState('')
  const [refinementIds, setRefinementIds] = useState([])
  const [addresses, setAddresses] = useState([])
  const [addressError, setAddressError] = useState('')
  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(undefined)

  const display = useMemo(
    () =>
      [
        [
          selectedAddress.address,
          selectedAddress.address2,
          selectedAddress.address3,
        ]
          .filter(Boolean)
          .join(' '),
        selectedAddress.city,
        selectedAddress.postalCode,
      ]
        .filter(Boolean)
        .join(', '),
    [selectedAddress]
  )

  const searchAddress = useCallback(() => {
    if (query.length < 3) {
      setAddresses([])
      setAddressError('')
      return
    }
    const key = refinementIds.at(-1) || query
    const cacheValue = addressCache.get(key)
    if (cacheValue) {
      setAddresses(cacheValue)
      setAddressError('')
      return
    }
    setLoading(true)
    badaAppointmentApi
      .searchAddresses({
        searchText: query,
        refinementId: refinementIds.at(-1),
        limit: 20,
      })
      .then((items) => {
        addressCache.set(key, items)
        setAddresses(items)
        setAddressError('')
      })
      .catch((e) => {
        const message = e?.message || 'An error occured when getting addresses'
        setAddresses([])
        setAddressError(message)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [query, refinementIds])

  useEffect(() => {
    setRefinementIds([])
    setAddresses([])
    setAddressError('')
  }, [query])

  const selectAddress = useCallback(
    (address) => {
      setDisabled(true)
      if (address.type !== badaAppointmentApi.ADDRESS_ITEM_TYPE) {
        setRefinementIds((ids) => [...ids, address.id])
        setTimeout(() => {
          setDisabled(false)
        }, 100)
        return
      }

      badaAppointmentApi
        .retrieveAddress(address.id)
        .then((data) => {
          onSelectAddress({
            address: data.line1 || data.company || data.city,
            address2: data.line2,
            address3: data.line3,
            postalCode: data.postalCode,
            city: data.city,
            country: data.countryIso2,
          })
          setQuery('')
        })
        .catch((e) => {
          const message =
            e?.message || 'An error occured when selecting the addresses'
          setAddressError(message)
        })
        .finally(() => {
          setTimeout(() => {
            setDisabled(false)
          }, 100)
        })
    },
    [onSelectAddress]
  )

  const focus = useCallback(() => {
    if (display) {
      setQuery((q) => q || display)
    } else {
      searchAddress()
    }
  }, [display, searchAddress])

  const blur = useCallback(
    (e) => {
      if (query && display) {
        setQuery('')
      }
      if (onBlur) {
        onBlur(e)
      }
    },
    [query, display]
  )

  useEffect(() => {
    const timeout = setTimeout(() => {
      searchAddress()
    }, 500)
    return () => {
      clearTimeout(timeout)
    }
  }, [searchAddress])

  return (
    <InputWrapper>
      <InputField
        name="address"
        autocomplete="address"
        value={query || display}
        onChange={(e) => {
          onClearAddress()
          setQuery(e.target.value)
        }}
        onFocus={focus}
        onBlur={blur}
        disabled={disabled}
      />
      {loading && (
        <SearchResult>
          <li>
            <Loader size="small" duration={3} />
          </li>
        </SearchResult>
      )}
      {!loading && !!addressError && (
        <SearchResult>
          <li>{addressError}</li>
        </SearchResult>
      )}
      {!loading && addresses.length > 0 && (
        <SearchResult>
          {refinementIds.length > 0 && (
            <ResultItem
              onClick={() => setRefinementIds((ids) => ids.slice(0, -1))}
            >
              <ArrowLeftIcon width={13} height={13} />
            </ResultItem>
          )}
          {addresses.map((address) => (
            <ResultItem key={address.id} onClick={() => selectAddress(address)}>
              {address.type === badaAppointmentApi.ADDRESS_ITEM_TYPE ? (
                <StyledLocationWidthIcon>
                  <LocationIcon width={20} height={20} />
                  <span>
                    {address.text}, {address.description}
                  </span>
                </StyledLocationWidthIcon>
              ) : (
                <>
                  <span>{address.text}</span>
                  <ArrowRightIcon width={13} height={13} />
                </>
              )}
            </ResultItem>
          ))}
        </SearchResult>
      )}
    </InputWrapper>
  )
}
